/**
 * Is a value produced by a Computation function assignable to the item it is
 * written into?
 *
 * Why this exists: the expression sandbox returns whatever the designer's
 * JavaScript evaluates to, and nothing between there and the input component
 * checks the shape. The canonical mistake is writing `today` instead of
 * `today()` — the expression then yields the *function object*, which is
 * happily stored in formData and handed to the item component.
 *
 * In Viedoc Me that currently produces something worse than a crash: it looks
 * like it worked. Measured, not assumed (see the probe notes on WI 104027):
 *
 *   - the field DISPLAYS the right date. react-hook-form's `useWatch` seeds its
 *     state with `useState(value)`, and React treats a function passed to
 *     `useState` as a lazy initializer — so it CALLS it, once, and the field
 *     shows `today()`'s result. Pure accident: it only holds for a zero-argument
 *     helper that happens to return a renderable value.
 *   - `formData` still holds the FUNCTION. `JSON.stringify` omits function
 *     properties entirely, so the value the patient sees on screen is silently
 *     dropped from the submitted payload.
 *
 * A displayed value that cannot be saved is a data-integrity problem, and no
 * part of the UI says anything. So the rule is: a value that cannot possibly
 * work is not written at all (the caller stores the item's empty value instead)
 * and is reported, naming the item and the expression. An empty field the
 * designer is told about beats a filled field that submits nothing.
 *
 * SCOPE — read this before treating the gate as a data-integrity guarantee.
 * It is client-side, and for COMPUTATIONS the server has the last word. The Me
 * submit path runs through the same Clinic.Legacy service Clinic uses
 * (PROApiService.SaveFormAsync -> FormDataSaveService.SaveFormAsync), which
 * calls ExecuteFunctionsAsync and RE-EXECUTES every MethodDef expression in
 * Jint, then re-runs the range checks via ValidateForm(validateRangeChecks:
 * true). So this gate governs what the participant SEES and SUBMITS — it stops
 * the crash, stops the silent submit-drop, and tells the designer — but it does
 * not decide what finally lands in the database for a computed item.
 *
 * For IMPUTATION DEFAULTS it IS authoritative: ExecuteFunctionsAsync selects
 * `.Where(ir => !String.IsNullOrWhiteSpace(ir.MethodOID))` and ODM carries
 * defaults under ImputationMethodOID, so a default is never recomputed
 * server-side. The client is the only line of defence there.
 *
 * Jint also applies coercions neither browser does — an array result becomes
 * `string.Join(",", …)` ("1,2") and a Date is timezone-converted
 * (ScriptRunner.Evaluate). Three evaluators, three rule sets; that divergence
 * is pre-existing and tracked separately from this WI.
 *
 * NOT the same defect as the Clinic preview's "t.format is not a function" from
 * the same UAT report. `.format` there is Clinic's String.prototype extension
 * used to build validation messages (quest.viewmodel.js), and Clinic has no
 * equivalent of React's accidental invocation. That one was fixed separately in
 * the monolith (PR !40050), which is also where the sibling
 * `checkFunctionValueType` lives — keep the two rule sets in step.
 *
 * Deliberately conservative — it rejects only values that CANNOT work, never
 * values that merely look unusual. Anything already accepted by the renderer
 * keeps working, so turning this on cannot break an existing study design:
 *
 *   - functions, symbols, plain objects and (except for Checkboxes) arrays are
 *     always rejected: no item type can render them.
 *   - a booleanic/textual value going into a NUMBER or DATE item is rejected,
 *     because the control cannot parse it.
 *   - a boolean going into a TEXT item is ALLOWED. "true" is a perfectly
 *     renderable string and a designer may well mean it.
 *   - a SCALAR code going into Checkboxes is ALLOWED, because the component
 *     wraps it and the box ticks. Clinic's half of this fix says the same.
 *   - codelist CODES are not validated. A well-typed code that isn't in the
 *     codelist is a different problem, out of scope here.
 *   - `null` / `undefined` / `""` are always fine — that is "no value", which
 *     every item type accepts.
 *
 * Call this with the value AFTER `normalizeComputed`, not the raw expression
 * result. normalizeComputed already maps `Date` → the "yyyy-MM-dd'T'HH:mm"
 * string the date items expect and non-finite numbers (`NaN` from `0/0`,
 * `±Infinity`) → `undefined`, so checking downstream of it keeps all of that
 * long-standing behaviour untouched and leaves this function to judge only
 * what actually reaches the component.
 */

// Item types grouped by what their control can actually accept. Types absent
// from this map (FileUpload, StaticText, SectionBreak, and anything added
// later) are not checked — a Computation on them is not a supported design, and
// silently rejecting a value for an unrecognised type would be worse than
// leaving it alone.
const ITEM_VALUE_CLASS = {
    Number: "number",
    Nrs: "number",
    VasScale: "number",
    Date: "date",
    DateAndTime: "date",
    Time: "time",
    SingleLineText: "text",
    ParagraphText: "text",
    Dropdown: "choice",
    RadioButtons: "choice",
    Checkboxes: "choiceList",
};

// What the designer should have produced, phrased for a warning message.
const EXPECTED_LABEL = {
    number: "a number",
    date: "a date",
    time: "a time",
    text: "text",
    choice: "a choice code",
    choiceList: "a list of choice codes",
};

// Accepts the shapes a date/datetime item can render: "2026-08-13",
// "2026-08-13T09:30", and the fuller ISO forms the API can send. Deliberately
// strict so a coded string ("001") or a bare year ("2026") is NOT mistaken for
// a date and waved through into a picker that would then crash.
const DATE_LIKE_RE =
    /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

// A time item stores "HH:mm" / "HH:mm:ss". A full datetime is also allowed:
// `normalizeComputed` turns any Date — e.g. from `now()` — into the datetime
// string, and rejecting that would break a design that works today.
const TIME_LIKE_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

const isNumericLike = (value) => {
    if (typeof value === "number") return Number.isFinite(value);
    // Number("") and Number(" ") are both 0, so a blank string must not pass
    // as the number zero.
    if (typeof value !== "string" || value.trim() === "") return false;
    return Number.isFinite(Number(value));
};

const isDateLike = (value) =>
    value instanceof Date
        ? !Number.isNaN(value.getTime())
        : typeof value === "string" && DATE_LIKE_RE.test(value);

const isTimeLike = (value) =>
    value instanceof Date
        ? !Number.isNaN(value.getTime())
        : typeof value === "string" &&
          (TIME_LIKE_RE.test(value) || DATE_LIKE_RE.test(value));

const isCode = (value) => typeof value === "string" || typeof value === "number";

/**
 * Describes what the expression actually produced, phrased for a warning
 * message. Kept blunt on purpose: the designer needs to recognise their own
 * mistake ("a function" → "I forgot the ()"), not read a type name.
 */
export function describeValue(value) {
    if (value === null) return "null";
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? "an invalid date" : "a date";
    }
    if (Array.isArray(value)) return "a list";
    switch (typeof value) {
        case "function":
            return "a function";
        case "undefined":
            return "no value";
        case "boolean":
            return "true/false";
        case "number":
            return Number.isFinite(value) ? "a number" : String(value);
        case "string":
            return "text";
        case "symbol":
            return "a symbol";
        default:
            return "an object";
    }
}

/**
 * @param {*} value      value to store, already through `normalizeComputed`
 * @param {string} itemType the item's declared type (see ITEM_VALUE_CLASS)
 * @returns {{ ok: true } | { ok: false, expected: string, actual: string }}
 */
export function checkComputedValueType(value, itemType) {
    // "No value" is assignable to everything; the caller treats it as a clear.
    if (value == null || value === "") return { ok: true };

    const valueClass = ITEM_VALUE_CLASS[itemType];
    // Unknown / unsupported item type — nothing to assert against.
    if (!valueClass) return { ok: true };

    const accepted = (() => {
        switch (valueClass) {
            case "number":
                return isNumericLike(value);
            case "date":
                return isDateLike(value);
            case "time":
                return isTimeLike(value);
            case "text":
                // Numbers and booleans stringify to something a text field can
                // show; functions/objects/arrays cannot.
                return (
                    typeof value === "string" ||
                    typeof value === "number" ||
                    typeof value === "boolean"
                );
            case "choice":
                return isCode(value);
            case "choiceList":
                // A scalar code is accepted, not just a list. `Checkboxes.jsx`
                // wraps one itself (`Array.isArray(value) ? value : [value]`),
                // so `return("2")` already ticks the "2" box today — and the
                // same expression is the CORRECT form for RadioButtons and
                // Dropdown, so writing it here is an easy mistake to make.
                // Rejecting it would clear a design that works, which the
                // "only reject what CANNOT work" bar above forbids. Clinic's
                // half of this fix reached the same conclusion independently
                // (quest.viewmodel.js, `kind === "list"`) and the two must not
                // diverge. See PR !40046 thread 290103.
                return (
                    isCode(value) ||
                    (Array.isArray(value) && value.every(isCode))
                );
            default:
                return true;
        }
    })();

    if (accepted) return { ok: true };

    return {
        ok: false,
        expected: EXPECTED_LABEL[valueClass] ?? "a different type",
        actual: describeValue(value),
    };
}

/**
 * One-line, designer-facing description of a mismatch. Shared by the preview
 * panel and the console/telemetry message so the wording cannot drift.
 *
 * `source` changes only the closing sentence, because the two callers do
 * different things: a Computation's unusable result replaces whatever was
 * stored, while an Imputation default that cannot be applied simply is not
 * applied — saying "cleared" there would describe something that never
 * happened.
 *
 * @param {{
 *   oid: string, label?: string, expected: string, actual: string,
 *   source?: "computation" | "default",
 * }} m
 */
export function formatTypeMismatch({ oid, label, expected, actual, source }) {
    const name = label ? `${oid} (${label})` : oid;
    const outcome =
        source === "default"
            ? "The default was not applied."
            : "The value was cleared.";
    return `${name}: the function returned ${actual}, but this item needs ${expected}. ${outcome}`;
}

// Exported for tests and for anyone needing to know whether a type is checked
// at all.
export const isTypeCheckedItem = (itemType) =>
    Object.hasOwn(ITEM_VALUE_CLASS, itemType);
